/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  TerminalSessionRegistry,
  type TerminalScope,
  type TerminalSession,
  type TerminalResourceInitializer,
} from './terminal-sessions.js';
import type { TerminalAgentMetadata } from './terminal-metadata.js';
import { dispatchTeardown } from '../utils/auth.js';

/** Document focus observation is not a guarantee of desktop foreground activation. */
export type TerminalFocusResult = 'document-focused' | 'not-confirmed';
export interface TerminalCoordinatorAdapter {
  initialize: TerminalResourceInitializer;
  /** Owner-only creation bridge; must return this registry's requested entry. */
  create?(
    registry: TerminalSessionRegistry,
    agentId: string,
    options?: { deferConnect?: boolean }
  ): TerminalSession;
  /**
   * Activate the retained single-pane presentation. Guard async work with signal.
   * Combine it with host navigation guards and REJECT a canceled selection so the
   * coordinator does not request focus. This signal covers document/account teardown.
   */
  select(session: TerminalSession, signal: AbortSignal, requestId?: string): void | Promise<void>;
  /** Optional host activation adapter. Must report observation, not selection success. */
  focus?(): Promise<TerminalFocusResult>;
}
export interface TerminalOpenResult {
  readonly status:
    | 'selected'
    | 'pending'
    | 'unsupported'
    | 'stopped'
    | 'request-id-conflict'
    | 'selection-failed';
  readonly requestId: string;
  readonly agentId: string;
  readonly generation: string | null;
  readonly focus: TerminalFocusResult;
}
interface Request {
  agentId: string;
  generation: string | null;
  done: boolean;
  result: Promise<TerminalOpenResult>;
  resolve(result: TerminalOpenResult): void;
}
interface Message {
  key: string;
  type: 'discover' | 'owner' | 'open' | 'ack' | 'account-teardown' | 'take-over' | 'released';
  requestId: string;
  agentId: string;
  generation: string | null;
  status?: TerminalOpenResult['status'];
  focus?: TerminalFocusResult;
}
/**
 * How long a window moving the terminals to itself waits for the owner to
 * confirm it closed its streams, and then for the Web Lock to reach it.
 */
export const MOVE_RELEASE_TIMEOUT_MS = 5000;
export const MOVE_OWNERSHIP_TIMEOUT_MS = 5000;

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const token = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= 256;

/**
 * One instance per authenticated document lifetime. Construct from trusted bootstrap
 * identity, never from a route or peer message. Keep it through SPA mode changes.
 * Same-origin peers are not an authentication boundary; the Hub authorizes attaches.
 */
export class TerminalCoordinator {
  readonly coordinationKey: string;
  private readonly registry: TerminalSessionRegistry;
  private channel: BroadcastChannel | null = null;
  private available = false;
  private ownerGeneration: string | null = null;
  private stopped = false;
  private claiming: Promise<boolean> | null = null;
  private release: (() => void) | null = null;
  private waitingAbort: AbortController | null = null;
  private readonly lifetime = new AbortController();
  private readonly requests = new Map<string, Request>();
  private readonly executed = new Map<
    string,
    { agentId: string; result: Promise<TerminalOpenResult> }
  >();
  private readonly _unsupportedReason: string | null;
  /** Pending take-over requests from this window, by request ID. */
  private readonly releaseWaiters = new Map<
    string,
    { agentId: string; asked: boolean; finish: (released: boolean) => void }
  >();
  /** Called whenever this window becomes the owner. */
  private readonly ownershipWaiters = new Set<() => void>();
  private relinquished: (() => void) | null = null;

  constructor(
    scope: TerminalScope,
    private readonly adapter: TerminalCoordinatorAdapter
  ) {
    // Reuse registry validation; normalization must match its origin/base-path scope.
    this.registry = new TerminalSessionRegistry(scope);
    const hubUrl = new URL(scope.hubUrl).href.replace(/\/+$/, '') + '/';
    this.coordinationKey = `terminal-owner:v1:${JSON.stringify([hubUrl, scope.accountId])}`;
    if (!globalThis.isSecureContext) {
      this._unsupportedReason = 'Terminal coordination requires a secure context (HTTPS).';
    } else if (!navigator.locks) {
      this._unsupportedReason = 'Terminal coordination requires the Web Lock API.';
    } else if (typeof BroadcastChannel !== 'function') {
      this._unsupportedReason = 'Terminal coordination requires BroadcastChannel support.';
    } else {
      try {
        this.channel = new BroadcastChannel(this.coordinationKey);
        this.channel.onmessage = (event: MessageEvent<unknown>): void => this.receive(event.data);
        this.available = true;
        this._unsupportedReason = null;
      } catch {
        this._unsupportedReason = 'Terminal coordination channel could not be created.';
      }
    }
    if (!this._unsupportedReason) this._unsupportedReason = null;
    window.addEventListener('pagehide', this.onPageHide);
  }

  get supported(): boolean {
    return this.available;
  }
  /**
   * Why coordination is unavailable. `null` when coordination is supported.
   * Display to the user instead of silently falling back to non-singleton behavior.
   */
  get unsupportedReason(): string | null {
    return this._unsupportedReason;
  }
  get isOwner(): boolean {
    return !this.stopped && this.ownerGeneration !== null;
  }
  get generation(): string | null {
    return this.ownerGeneration;
  }
  /** Only the owner's retained host receives document-local session handles. */
  get sessions(): readonly TerminalSession[] {
    return this.isOwner ? this.registry.list() : [];
  }

  /**
   * Current metadata snapshot for agentId, for callers (terminal-persistence)
   * that need to know availability (e.g. 'deleted') without holding a
   * reference to the private registry. Undefined when the agent is not
   * currently retained (no open or restored entry).
   */
  metadataFor(agentId: string): TerminalAgentMetadata | undefined {
    return this.registry.metadata.get(agentId);
  }

  /**
   * Public wrapper over the private claim() (ifAvailable, plus the queued
   * wait on failure), for the terminal-persistence restore path. Resolves
   * false, without calling navigator.locks, when coordination is
   * unsupported (insecure context, no Web Locks, no BroadcastChannel) or
   * this coordinator is torn down. If the lock request rejects, it sets
   * available = false (as route() does) and resolves false. Never rejects.
   */
  async claimOwnership(): Promise<boolean> {
    if (!this.available || this.stopped) return false;
    try {
      return await this.claim();
    } catch {
      this.available = false;
      return false;
    }
  }

  /**
   * Forwards registry.subscribe, including its immediate synchronous first
   * call. Delivers only while isOwner && !tornDown — a non-owner, or a torn
   * down coordinator, gets no callbacks (including none from sessions
   * closing inside stop()). Returns an unsubscribe function.
   */
  subscribeSessions(cb: (sessions: readonly TerminalSession[]) => void): () => void {
    return this.registry.subscribe((sessions) => {
      if (!this.isOwner || this.stopped) return;
      cb(sessions);
    });
  }

  /**
   * Restores background entries for terminal-persistence's merge. Checks
   * isOwner && !tornDown synchronously; if that check fails, creates nothing
   * and returns []. Otherwise, for each id in agentIds not already present
   * in the registry, calls adapter.create with deferConnect true for every
   * id except connectAgentId. Returns the sessions for agentIds, in order
   * (existing sessions included).
   */
  restoreEntries(
    agentIds: readonly string[],
    opts: { connectAgentId: string | null }
  ): readonly TerminalSession[] {
    if (!this.isOwner || this.stopped) return [];
    const existing = new Map(
      this.registry.list().map((session) => [session.state.agentId, session])
    );
    const result: TerminalSession[] = [];
    for (const agentId of agentIds) {
      let session = existing.get(agentId);
      if (!session) {
        session = this.adapter.create
          ? this.adapter.create(this.registry, agentId, {
              deferConnect: agentId !== opts.connectAgentId,
            })
          : this.registry.open(agentId, this.adapter.initialize, {
              deferConnect: agentId !== opts.connectAgentId,
            });
        existing.set(agentId, session);
      }
      result.push(session);
    }
    return result;
  }

  /**
   * Creates entries in this window's own registry while ANOTHER window owns
   * the terminals, for a move to this window (ptone/scion#3328). Mirrors
   * restoreEntries, but only for a non-owner: each entry authorizes and
   * attaches on its own, and nothing is taken from the owner. Every id
   * except connectAgentId is created idle (deferConnect). Returns [] for an
   * owner, a torn down coordinator, or when coordination is unsupported.
   */
  openForMove(
    agentIds: readonly string[],
    opts: { connectAgentId: string | null }
  ): readonly TerminalSession[] {
    if (this.isOwner || this.stopped || !this.available) return [];
    const existing = new Map(
      this.registry.list().map((session) => [session.state.agentId, session])
    );
    const result: TerminalSession[] = [];
    for (const agentId of agentIds) {
      if (!uuid.test(agentId)) continue;
      const id = agentId.toLowerCase();
      let session = existing.get(id);
      if (!session) {
        const deferConnect = id !== opts.connectAgentId?.toLowerCase();
        session = this.adapter.create
          ? this.adapter.create(this.registry, id, { deferConnect })
          : this.registry.open(id, this.adapter.initialize, { deferConnect });
        existing.set(id, session);
      }
      result.push(session);
    }
    return result;
  }

  /**
   * Asks the owning window to close its streams and release ownership, for
   * a move to this window. Call it only once this window's own streams are
   * open. Resolves true when the owner confirms; false on timeout, when
   * this window is already the owner, or when coordination is unavailable.
   * The owner keeps its streams and the Web Lock until it gets this request.
   *
   * The take-over names the owner's generation, learned with the same
   * discover/owner exchange open() uses, so a window that becomes the owner
   * while the message is in flight does not act on it. Resolves false at
   * once if this window becomes the owner meanwhile (the owner went away).
   */
  requestRelease(agentId: string, timeoutMs = MOVE_RELEASE_TIMEOUT_MS): Promise<boolean> {
    if (this.isOwner || this.stopped || !this.available || !uuid.test(agentId))
      return Promise.resolve(false);
    const requestId = crypto.randomUUID();
    return new Promise<boolean>((resolve) => {
      const finish = (released: boolean): void => {
        clearTimeout(timer);
        this.releaseWaiters.delete(requestId);
        resolve(released);
      };
      const timer = setTimeout(() => finish(false), timeoutMs);
      this.releaseWaiters.set(requestId, { agentId: agentId.toLowerCase(), asked: false, finish });
      this.send({
        key: this.coordinationKey,
        type: 'discover',
        requestId,
        agentId: agentId.toLowerCase(),
        generation: null,
      });
    });
  }

  /**
   * Waits for this window to hold the Web Lock, without taking it early: a
   * held lock is only ever acquired through the queued wait, after the
   * holder releases it. Resolves false on timeout, teardown, or when
   * coordination is unavailable.
   */
  awaitOwnership(timeoutMs = MOVE_OWNERSHIP_TIMEOUT_MS): Promise<boolean> {
    if (this.isOwner) return Promise.resolve(true);
    if (this.stopped || !this.available) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      const done = (owned: boolean): void => {
        clearTimeout(timer);
        this.ownershipWaiters.delete(onOwner);
        resolve(owned);
      };
      const onOwner = (): void => done(!this.stopped && this.isOwner);
      const timer = setTimeout(() => done(this.isOwner), timeoutMs);
      this.ownershipWaiters.add(onOwner);
      // Queue a wait if none is queued yet (claim() tries ifAvailable, then
      // queues); never steals a held lock.
      if (!this.waitingAbort && !this.claiming)
        void this.claim().catch(() => {
          /* reported as a timeout */
        });
    });
  }

  /**
   * Sets the callback run after this window gave its terminals to another
   * window: its sessions are closed and it no longer owns them.
   */
  onRelinquished(cb: (() => void) | null): void {
    this.relinquished = cb;
  }

  private notifyOwnership(): void {
    for (const waiter of [...this.releaseWaiters.values()]) waiter.finish(false);
    for (const cb of [...this.ownershipWaiters]) cb();
  }

  /**
   * Owner side of a move: stop owning, close every session without sending
   * input to the terminal, release the Web Lock, confirm to the requester,
   * and queue to become the owner again later. Ownership is cleared before
   * the sessions close, so owner-only subscribers (persistence) see nothing.
   */
  private relinquish(message: Message): void {
    if (!this.isOwner || message.generation !== this.ownerGeneration) return;
    const sessions = this.registry.list();
    this.ownerGeneration = null;
    for (const session of sessions) {
      try {
        session.close('navigation');
      } catch (error) {
        console.error('[Terminal] closing a session for a move failed:', error);
      }
    }
    const release = this.release;
    this.release = null;
    release?.();
    this.executed.clear();
    this.send({
      key: this.coordinationKey,
      type: 'released',
      requestId: message.requestId,
      agentId: message.agentId,
      generation: null,
    });
    try {
      this.relinquished?.();
    } catch (error) {
      console.error('[Terminal] move listener failed:', error);
    }
    this.waitForOwnership();
  }

  /**
   * Retry with the SAME request ID after pending; a new user intent gets a new ID.
   * Timeout is not cancellation. The open intent survives requester navigation;
   * no per-request cross-tab cancellation or automatic retry is performed.
   */
  async open(
    agentId: string,
    suppliedRequestId?: string,
    timeoutMs = 1000
  ): Promise<TerminalOpenResult> {
    if (!uuid.test(agentId)) throw new Error('Terminal requires an agent UUID.');
    if (suppliedRequestId !== undefined && !token(suppliedRequestId))
      throw new Error('Terminal request ID required (maximum 256 characters).');
    if (!Number.isFinite(timeoutMs) || timeoutMs < 0)
      throw new Error('Invalid acknowledgment timeout.');
    agentId = agentId.toLowerCase();
    // No request is submitted in these terminal states, so an omitted ID needs
    // only a result marker. Insecure HTTP does not expose crypto.randomUUID.
    const requestId =
      suppliedRequestId ?? (this.stopped || !this.available ? 'unsubmitted' : crypto.randomUUID());
    const outcome = (status: TerminalOpenResult['status']): TerminalOpenResult => ({
      status,
      agentId,
      requestId,
      generation: null,
      focus: 'not-confirmed',
    });
    if (this.stopped) return outcome('stopped');
    if (!this.available) return outcome('unsupported');
    let request = this.requests.get(requestId);
    if (request && request.agentId !== agentId) return outcome('request-id-conflict');
    if (!request) {
      let resolve!: Request['resolve'];
      const result = new Promise<TerminalOpenResult>((done) => {
        resolve = done;
      });
      request = { agentId, generation: null, done: false, result, resolve };
      this.requests.set(requestId, request);
    }
    if (!request.done) void this.route(requestId, request);
    // Bound the whole operation, including a delayed lock callback. Expiration only
    // changes the caller's result; it never releases, steals or assumes a lock.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<TerminalOpenResult>((resolve) => {
      timer = setTimeout(() => resolve(outcome('pending')), timeoutMs);
    });
    return Promise.race([request.result, deadline]).finally(() => clearTimeout(timer));
  }

  private async route(requestId: string, request: Request): Promise<void> {
    try {
      const owner = await this.claim();
      if (this.stopped || request.done) return;
      if (owner) {
        request.generation = this.ownerGeneration;
        this.execute({
          key: this.coordinationKey,
          type: 'open',
          requestId,
          agentId: request.agentId,
          generation: request.generation,
        });
      } else {
        this.send({
          key: this.coordinationKey,
          type: 'discover',
          requestId,
          agentId: request.agentId,
          generation: null,
        });
      }
    } catch {
      this.available = false;
      this.finish(request, {
        status: 'unsupported',
        requestId,
        agentId: request.agentId,
        generation: null,
        focus: 'not-confirmed',
      });
    }
  }

  private claim(): Promise<boolean> {
    if (this.isOwner) return Promise.resolve(true);
    if (this.claiming) return this.claiming;
    this.claiming = new Promise<boolean>((resolve, reject) => {
      void navigator.locks
        .request(this.coordinationKey, { mode: 'exclusive', ifAvailable: true }, async (lock) => {
          if (!lock || this.stopped) {
            // Lock held by another tab; queue a waiting request so this tab
            // acquires ownership when the current owner exits or crashes.
            // Frozen owners keep the lock; this never steals from them.
            if (!lock && !this.stopped) this.waitForOwnership();
            resolve(false);
            return;
          }
          this.ownerGeneration = crypto.randomUUID();
          const held = new Promise<void>((done) => {
            this.release = done;
          });
          resolve(true);
          this.notifyOwnership();
          await held;
          // Lock released (by stop() or browser tab destruction).
          // Ensure stale owner state is cleared even if stop() was not
          // the caller (e.g. the browser reclaimed the lock).
          this.ownerGeneration = null;
          this.release = null;
        })
        .catch(reject);
    }).finally(() => {
      this.claiming = null;
    });
    return this.claiming;
  }

  /**
   * Queue a non-ifAvailable lock request. When the current owner releases
   * the lock (tab close, crash, navigation away), this fires and makes the
   * current tab the new owner. The request is cancelled on stop().
   *
   * This never races with a frozen owner — a frozen tab keeps its Web Lock.
   */
  private waitForOwnership(): void {
    if (this.waitingAbort) return; // Already waiting
    this.waitingAbort = new AbortController();
    void navigator.locks
      .request(
        this.coordinationKey,
        { mode: 'exclusive', signal: this.waitingAbort.signal },
        async (lock) => {
          this.waitingAbort = null;
          if (!lock || this.stopped) return;
          this.ownerGeneration = crypto.randomUUID();
          const held = new Promise<void>((done) => {
            this.release = done;
          });
          this.notifyOwnership();
          await held;
          this.ownerGeneration = null;
          this.release = null;
        }
      )
      .catch(() => {
        // AbortError from stop() cancellation is expected.
        this.waitingAbort = null;
      });
  }

  private send(message: Message): void {
    this.channel?.postMessage(message);
  }

  private receive(data: unknown): void {
    if (this.stopped || !data || typeof data !== 'object') return;
    const message = data as Partial<Message>;
    if (message.key !== this.coordinationKey) return;
    // Account-teardown is a coordination-level signal that does not target a
    // specific agent. Validate only the key and type, not the agent UUID.
    if (message.type === 'account-teardown') {
      try {
        this.stop();
      } catch (e) {
        console.error('[Teardown] cleanup error:', e);
      }
      // Notify the workspace layer so it hides UI and sets the torn-down guard.
      // The main.ts listener is idempotent (accountTornDown guard), so
      // double-dispatch from both local and received paths is safe.
      dispatchTeardown('logout');
      return;
    }
    if (
      !token(message.requestId) ||
      typeof message.agentId !== 'string' ||
      !uuid.test(message.agentId)
    )
      return;
    if (message.type === 'take-over') {
      if (token(message.generation)) this.relinquish(message as Message);
      return;
    }
    if (message.type === 'released') {
      this.releaseWaiters.get(message.requestId)?.finish(true);
      return;
    }
    const release = this.releaseWaiters.get(message.requestId);
    if (release && message.type === 'owner' && token(message.generation)) {
      // The owner answered this window's discover: ask that generation, once.
      if (release.asked || release.agentId !== message.agentId) return;
      release.asked = true;
      this.send({
        key: this.coordinationKey,
        type: 'take-over',
        requestId: message.requestId,
        agentId: release.agentId,
        generation: message.generation,
      });
      return;
    }
    const request = this.requests.get(message.requestId);
    if (message.type === 'discover' && this.isOwner) {
      this.send({
        key: this.coordinationKey,
        type: 'owner',
        requestId: message.requestId,
        agentId: message.agentId,
        generation: this.ownerGeneration,
      });
    } else if (message.type === 'owner' && token(message.generation)) {
      if (!request || request.done || request.agentId !== message.agentId) return;
      request.generation = message.generation;
      this.send({
        key: this.coordinationKey,
        type: 'open',
        requestId: message.requestId,
        agentId: request.agentId,
        generation: message.generation,
      });
    } else if (message.type === 'open' && token(message.generation)) {
      this.execute({
        key: this.coordinationKey,
        type: 'open',
        requestId: message.requestId,
        agentId: message.agentId.toLowerCase(),
        generation: message.generation,
      });
    } else if (message.type === 'ack') {
      if (
        !request ||
        request.done ||
        !token(message.generation) ||
        request.generation !== message.generation ||
        request.agentId !== message.agentId ||
        !['selected', 'selection-failed', 'request-id-conflict'].includes(message.status ?? '') ||
        !['document-focused', 'not-confirmed'].includes(message.focus ?? '')
      )
        return;
      this.finish(request, {
        status: message.status!,
        focus: message.focus!,
        agentId: message.agentId,
        requestId: message.requestId,
        generation: message.generation,
      });
    }
  }

  private execute(message: Message): void {
    if (!this.isOwner || message.generation !== this.ownerGeneration) return;
    const existing = this.executed.get(message.requestId);
    if (existing && existing.agentId !== message.agentId) {
      this.deliver(message, { ...message, status: 'request-id-conflict', focus: 'not-confirmed' });
      return;
    }
    if (!existing) {
      // Install deduplication before registry/UI callbacks, including reentrant opens.
      const result = Promise.resolve().then(async (): Promise<TerminalOpenResult> => {
        const reply: TerminalOpenResult = {
          requestId: message.requestId,
          agentId: message.agentId,
          generation: message.generation,
          status: 'stopped',
          focus: 'not-confirmed',
        };
        if (!this.isOwner) return reply;
        try {
          const session =
            this.registry.list().find((entry) => entry.state.agentId === message.agentId) ??
            (this.adapter.create
              ? this.adapter.create(this.registry, message.agentId)
              : this.registry.open(message.agentId, this.adapter.initialize));
          if (session.state.agentId !== message.agentId || !this.registry.list().includes(session))
            throw new Error('Terminal adapter returned a foreign session.');
          await this.adapter.select(session, this.lifetime.signal, message.requestId);
          if (!this.isOwner) return reply;
          const focus = await this.focus();
          return { ...reply, status: 'selected', focus };
        } catch {
          return { ...reply, status: 'selection-failed' };
        }
      });
      this.executed.set(message.requestId, { agentId: message.agentId, result });
    }
    void this.executed
      .get(message.requestId)!
      .result.then((result) => this.deliver(message, result));
  }

  private async focus(): Promise<TerminalFocusResult> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      // A host adapter must not delay acknowledgment indefinitely.
      const timeout = new Promise<TerminalFocusResult>((resolve) => {
        timer = setTimeout(() => resolve('not-confirmed'), 150);
      });
      return await Promise.race([
        this.adapter.focus ? this.adapter.focus() : observeFocus(),
        timeout,
      ]);
    } catch {
      return 'not-confirmed';
    } finally {
      clearTimeout(timer);
    }
  }

  private deliver(message: Message, result: TerminalOpenResult): void {
    if (!this.isOwner || message.generation !== this.ownerGeneration) return;
    const ack: Message = { ...message, ...result, type: 'ack' };
    this.receive(ack); // BroadcastChannel does not echo to the sending object.
    this.send(ack);
  }

  private finish(request: Request, result: TerminalOpenResult): void {
    request.done = true;
    request.resolve(result);
  }

  /**
   * Whether this coordinator has been torn down for the current account.
   * Guards against stale callbacks recreating sessions after teardown.
   */
  get tornDown(): boolean {
    return this.stopped;
  }

  /**
   * Cross-tab account teardown. Broadcasts teardown to all same-origin peers
   * via BroadcastChannel, then disposes the local coordinator. Called from
   * the logout/auth-expiry path — never from route or mode changes.
   *
   * Both owner and non-owner tabs may call this. The broadcast ensures the
   * owner receives the teardown even if the logout happened in a non-owner tab.
   */
  teardownAccount(): void {
    if (this.stopped) return;
    // Broadcast before stopping so the channel is still open.
    this.send({
      key: this.coordinationKey,
      type: 'account-teardown',
      requestId: 'teardown',
      agentId: '',
      generation: this.ownerGeneration,
    });
    this.stop();
  }

  private readonly onPageHide = (): void => this.stop();

  /**
   * Document/account teardown ONLY. Never call on route or mode changes.
   * Attempts every session close; throws AggregateError on failure and retains the lock.
   */
  stop(): void {
    if (this.stopped) return;
    // Every registry entry, not only an owner's: a non-owner can hold entries
    // it opened for a move to this window (openForMove).
    const sessions = this.registry.list();
    this.stopped = true;
    this.lifetime.abort();
    // Cancel any queued ownership wait before touching other state.
    // The AbortController fires synchronously, preventing the queued
    // lock callback from racing with teardown.
    try {
      this.waitingAbort?.abort();
    } catch {
      /* AbortController.abort() is specified not to throw, but guard. */
    }
    this.waitingAbort = null;
    window.removeEventListener('pagehide', this.onPageHide);
    this.channel?.close();
    this.channel = null;
    this.notifyOwnership();
    this.releaseWaiters.clear();
    this.ownershipWaiters.clear();
    this.relinquished = null;
    for (const [requestId, request] of this.requests) {
      if (!request.done)
        this.finish(request, {
          status: 'stopped',
          requestId,
          agentId: request.agentId,
          generation: request.generation,
          focus: 'not-confirmed',
        });
    }
    // One renderer failure must not leave other transports live after teardown.
    // Report all failures only after every session has had a chance to close.
    const failures: unknown[] = [];
    for (const session of sessions) {
      try {
        session.close();
      } catch (error) {
        failures.push(error);
      }
    }
    // Keep the lock until document exit on ANY failure. Repeated stop is a no-op;
    // it must not retry failed disposal or accidentally release this authority.
    if (failures.length) throw new AggregateError(failures, 'Terminal session teardown failed.');
    // Cleanup precedes voluntary lock release: ownerGeneration is cleared
    // before release() so no stale callback can see this tab as owner.
    this.ownerGeneration = null;
    this.release?.();
    this.requests.clear();
    this.executed.clear();
  }
}

async function observeFocus(): Promise<TerminalFocusResult> {
  window.focus();
  await new Promise<void>((resolve) => setTimeout(resolve, 100));
  return document.visibilityState === 'visible' && document.hasFocus()
    ? 'document-focused'
    : 'not-confirmed';
}
